import {
  type KeyboardEvent,
  type ReactNode,
  type UIEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import "./AnimatedList.css";

interface AnimatedItemProps {
  children: ReactNode;
  disabled: boolean;
  index: number;
  selected: boolean;
  onFocus: () => void;
  onMouseEnter: () => void;
  onClick: () => void;
}

function AnimatedItem({
  children,
  disabled,
  index,
  selected,
  onFocus,
  onMouseEnter,
  onClick,
}: AnimatedItemProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const inView = useInView(ref, { amount: 0.45 });
  const reduceMotion = useReducedMotion();

  return (
    <motion.button
      ref={ref}
      type="button"
      data-index={index}
      data-selected={selected}
      disabled={disabled}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onClick={onClick}
      initial={reduceMotion ? false : { scale: 0.88, opacity: 0 }}
      animate={reduceMotion || inView ? { scale: 1, opacity: 1 } : { scale: 0.88, opacity: 0 }}
      transition={{ duration: 0.2, delay: Math.min(index, 4) * 0.04 }}
      className="animated-list-item"
    >
      {children}
    </motion.button>
  );
}

export interface AnimatedListProps<T> {
  items: T[];
  renderItem: (item: T, index: number, selected: boolean) => ReactNode;
  getItemKey: (item: T, index: number) => string;
  onItemSelect?: (item: T, index: number) => void;
  isItemDisabled?: (item: T, index: number) => boolean;
  showGradients?: boolean;
  enableArrowNavigation?: boolean;
  className?: string;
  itemClassName?: string;
  displayScrollbar?: boolean;
  initialSelectedIndex?: number;
  ariaLabel?: string;
}

export default function AnimatedList<T>({
  items,
  renderItem,
  getItemKey,
  onItemSelect,
  isItemDisabled,
  showGradients = true,
  enableArrowNavigation = true,
  className = "",
  itemClassName = "",
  displayScrollbar = true,
  initialSelectedIndex = -1,
  ariaLabel = "Lessons",
}: AnimatedListProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = useState(initialSelectedIndex);
  const [keyboardNav, setKeyboardNav] = useState(false);
  const [topGradientOpacity, setTopGradientOpacity] = useState(0);
  const [bottomGradientOpacity, setBottomGradientOpacity] = useState(0);

  const itemIsDisabled = useCallback(
    (index: number) => index < 0 || index >= items.length || Boolean(isItemDisabled?.(items[index], index)),
    [isItemDisabled, items],
  );

  const updateGradients = useCallback((element: HTMLDivElement) => {
    const { scrollTop, scrollHeight, clientHeight } = element;
    setTopGradientOpacity(Math.min(scrollTop / 48, 1));

    const bottomDistance = scrollHeight - scrollTop - clientHeight;
    setBottomGradientOpacity(scrollHeight <= clientHeight ? 0 : Math.min(bottomDistance / 48, 1));
  }, []);

  const handleScroll = useCallback(
    (event: UIEvent<HTMLDivElement>) => updateGradients(event.currentTarget),
    [updateGradients],
  );

  const handleItemSelect = useCallback(
    (item: T, index: number) => {
      if (itemIsDisabled(index)) return;
      setSelectedIndex(index);
      onItemSelect?.(item, index);
    },
    [itemIsDisabled, onItemSelect],
  );

  const findEnabledIndex = useCallback(
    (startIndex: number, direction: 1 | -1) => {
      let nextIndex = startIndex;
      while (nextIndex >= 0 && nextIndex < items.length) {
        if (!itemIsDisabled(nextIndex)) return nextIndex;
        nextIndex += direction;
      }
      return -1;
    },
    [itemIsDisabled, items.length],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!enableArrowNavigation) return;

      let nextIndex = -1;
      if (event.key === "ArrowDown") {
        nextIndex = findEnabledIndex(Math.max(selectedIndex + 1, 0), 1);
      } else if (event.key === "ArrowUp") {
        nextIndex = findEnabledIndex(selectedIndex < 0 ? items.length - 1 : selectedIndex - 1, -1);
      } else if (event.key === "Home") {
        nextIndex = findEnabledIndex(0, 1);
      } else if (event.key === "End") {
        nextIndex = findEnabledIndex(items.length - 1, -1);
      } else {
        return;
      }

      if (nextIndex < 0) return;
      event.preventDefault();
      setKeyboardNav(true);
      setSelectedIndex(nextIndex);
    },
    [enableArrowNavigation, findEnabledIndex, items.length, selectedIndex],
  );

  useEffect(() => {
    setSelectedIndex(initialSelectedIndex);
  }, [initialSelectedIndex]);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    updateGradients(list);
    if (typeof ResizeObserver === "undefined") return;

    const resizeObserver = new ResizeObserver(() => updateGradients(list));
    resizeObserver.observe(list);
    return () => resizeObserver.disconnect();
  }, [items.length, updateGradients]);

  useEffect(() => {
    if (!keyboardNav || selectedIndex < 0 || !listRef.current) return;

    const container = listRef.current;
    const selectedItem = container.querySelector<HTMLButtonElement>(`[data-index="${selectedIndex}"]`);
    if (!selectedItem) return;

    selectedItem.focus({ preventScroll: true });
    const margin = 48;
    const itemTop = selectedItem.offsetTop;
    const itemBottom = itemTop + selectedItem.offsetHeight;

    if (itemTop < container.scrollTop + margin) {
      container.scrollTo({ top: Math.max(itemTop - margin, 0), behavior: "smooth" });
    } else if (itemBottom > container.scrollTop + container.clientHeight - margin) {
      container.scrollTo({
        top: itemBottom - container.clientHeight + margin,
        behavior: "smooth",
      });
    }

    setKeyboardNav(false);
  }, [keyboardNav, selectedIndex]);

  return (
    <div className={`animated-list-container ${className}`}>
      <div
        ref={listRef}
        className={`animated-list-scroll ${displayScrollbar ? "" : "no-scrollbar"}`}
        onScroll={handleScroll}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
      >
        {items.map((item, index) => (
          <AnimatedItem
            key={getItemKey(item, index)}
            index={index}
            disabled={itemIsDisabled(index)}
            selected={selectedIndex === index}
            onFocus={() => setSelectedIndex(index)}
            onMouseEnter={() => !itemIsDisabled(index) && setSelectedIndex(index)}
            onClick={() => handleItemSelect(item, index)}
          >
            <div className={`animated-list-item-content ${itemClassName}`}>
              {renderItem(item, index, selectedIndex === index)}
            </div>
          </AnimatedItem>
        ))}
      </div>

      {showGradients && (
        <>
          <div className="animated-list-top-gradient" style={{ opacity: topGradientOpacity }} />
          <div className="animated-list-bottom-gradient" style={{ opacity: bottomGradientOpacity }} />
        </>
      )}
    </div>
  );
}
